/* eslint-disable max-len */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import {
    API_BASE_URL,
    API_LOCAL_URL,
    API_PUBLIC_URL,
    API_VISUALIZATION_URL,
    TIME_OUT
} from './config';
import { updateRefreshModalShow, updateWarningMessage } from '../../redux/reducer/common';
import { updateAuthenticateState, updateFirstName } from '@/redux/reducer/soilUser';
import { soilGetSignUrl, soilGetUserToken } from '.';
import store from '../../redux';
import { updateOauthUrl } from '@/redux/reducer/weatherStation';

enum StatusCode {
    Unauthorized = 401,
    Forbidden = 403,
    TooManyRequests = 429,
    InternalServerError = 500,
    AddAnalysisError = 2002
}

enum GetTokenStatus {
    TokenUnRequest,
    TokenRequesting,
    TokenRequested
}

const headers: Readonly<Record<string, string | boolean>> = {
    Accept: 'application/json text/plain',
    'Content-Type': ' application/json;charset=utf-8',
    // 'Access-Control-Allow-Credentials': true,
    'X-Requested-With': 'XMLHttpRequest',
    'Access-Control-Allow-Origin': true
};

enum ServerType {
    ServerNormal,
    ServerUser,
    ServerPublic,
    ServerVisualization
}

class Http {
    private instance: AxiosInstance | null = null;
    private userInstance: AxiosInstance | null = null;
    private publicInstance: AxiosInstance | null = null;
    private visualizationInstance: AxiosInstance | null = null;
    private getTokenStatus: GetTokenStatus = GetTokenStatus.TokenUnRequest;
    private token: string = null;

    private get http(): AxiosInstance {
        return this.instance != null ? this.instance : this.initHttp(ServerType.ServerNormal);
    }

    private get userHttp(): AxiosInstance {
        return this.userInstance != null ? this.userInstance : this.initHttp(ServerType.ServerUser);
    }

    private get publicHttp(): AxiosInstance {
        return this.publicInstance != null ? this.publicInstance : this.initHttp(ServerType.ServerPublic);
    }

    private get visualizationHttp(): AxiosInstance {
        return this.visualizationInstance != null ? this.visualizationInstance : this.initHttp(ServerType.ServerVisualization);
    }

    private requests = [];

    private initHttp(type: ServerType) {
        let baseURL = API_BASE_URL;
        switch (type) {
            case ServerType.ServerNormal:
                baseURL = API_BASE_URL;
                break;
            case ServerType.ServerPublic:
                baseURL = API_PUBLIC_URL;
                break;
            case ServerType.ServerVisualization:
                baseURL = API_VISUALIZATION_URL;
                break;
            case ServerType.ServerUser:
                baseURL = API_LOCAL_URL;
                break;
        }

        const http = axios.create({
            baseURL,
            headers,
            timeout: 600000
            // this properties should open after the login is finish
            // withCredentials: true
        });

        http.interceptors.request.use(
            this.configTokenBeforeAll,
            (error) => {
                return Promise.reject(error);
            }
        );
        // request pre hook need to checkt the token is exist and add it to the request
        http.interceptors.request.use(
            this.configRequestPayloadFormat,
            (error) => {
                return Promise.reject(error);
            },
        );

        http.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                const response = this.handleError(error);
                return Promise.reject(response);
            }
        );

        // set the time out
        // http.defaults.timeout = TIME_OUT;

        switch (type) {
            case ServerType.ServerNormal:
                this.instance = http;
                break;
            case ServerType.ServerPublic:
                this.publicInstance = http;
                break;
            case ServerType.ServerVisualization:
                this.visualizationInstance = http;
                break;
            case ServerType.ServerUser:
                this.userInstance = http;
                break;
        }
        return http;
    }

    public request<T, R = AxiosResponse<T>>(
        config: AxiosRequestConfig
    ): Promise<R> {
        return this.http.request(config);
    }

    public get<T, R = AxiosResponse<T>>(
        url: string,
        data?: T,
        type = ServerType.ServerPublic,
    ): Promise<R> {
        this.http.defaults.responseType = 'json';
        // console.log('request test');
        switch (type) {
            case ServerType.ServerNormal:
                this.http.defaults.responseType = 'json';
                // return this.http.get<T, R>(url, data);
                return this.http.get<T, R>(url, {params: data});
                break;
            case ServerType.ServerUser:
                this.userHttp.defaults.responseType = 'json';
                // return this.userHttp.get<T, R>(url, data);
                return this.userHttp.get<T, R>(url, {params: data});
                break;
            case ServerType.ServerVisualization:
                this.visualizationHttp.defaults.responseType = 'json';
                return this.visualizationHttp.get<T, R>(url, {params: data});
                break;
            default:
                this.publicHttp.defaults.responseType = 'json';
                return this.publicHttp.get<T, R>(url, data);
                break;
        }
    }

    public post<T, R = AxiosResponse<T>>(
        url: string,
        data?: T,
        type = ServerType.ServerPublic,
        isJson = false,
        upload?: boolean
    ): Promise<R> {

        switch (type) {
            case ServerType.ServerNormal:
                this.http.defaults.responseType = 'json';
                return this.http.post<T, R>(url, data, { headers: { isJson, upload } });
                break;
            case ServerType.ServerUser:
                this.userHttp.defaults.responseType = 'json';
                return this.userHttp.post<T, R>(url, data, { headers: { isJson, upload } });
                break;
            case ServerType.ServerVisualization:
                this.visualizationHttp.defaults.responseType = 'json';
                return this.visualizationHttp.post<T, R>(url, data, { headers: { isJson, upload } });
                break;
            default:
                this.publicHttp.defaults.responseType = 'json';
                return this.publicHttp.post<T, R>(url, data, { headers: { isJson, upload } });
                break;
        }
    }

    public delete<T, R = AxiosResponse<T>>(
        url: string,
        config?: AxiosRequestConfig
    ): Promise<R> {
        this.http.defaults.responseType = 'json';
        return this.http.delete<T, R>(url, config);
    }

    // 'XLS' | 'XLSX' | 'PDF' | 'CSV'
    public download(
        url: string,
        data?: { selectedUserIDs?: string, listProfilesIDs?: number[], enumFileType?: 'XLS' | 'XLSX' | 'PDF' | 'CSV' },
    ): Promise<any> {
        return this.post(url, data, ServerType.ServerPublic);
    }
    //start added by Echo for download
    public downloadBlob<T, R = AxiosResponse<T>>(
        url: string,
        data?: T,
        type = ServerType.ServerPublic,
        isJson = false
    ) {
        let normalCacheHttp: AxiosInstance = this.http;
        let publicCacheHttp: AxiosInstance = this.publicHttp;
        let newHttp: AxiosInstance = null;
        switch (type) {
            case ServerType.ServerNormal:
                return new Promise((resolve) => {
                    newHttp = Object.assign({}, this.http);
                    newHttp.defaults.responseType = 'blob';
                    newHttp.post<T, AxiosResponse<{ config: string }>>(url, data, { headers: { isJson } }).then(response => {
                        console.log('response', response, data['fileName']);
                        this.downFile(response, data['fileName'], data['enumFileType']);
                        resolve(response);
                    });
                });
                break;

            case ServerType.ServerUser:
                normalCacheHttp = Object.assign({}, this.userHttp);
                normalCacheHttp.defaults.responseType = 'blob';
                return new Promise((resolve) => {
                    normalCacheHttp.post(url, data, { headers: { isJson } }).then(response => {

                        this.downFile(response, data['fileName']);
                        resolve(response);
                    });
                });
                break;
            default:
                publicCacheHttp = Object.assign({}, this.publicHttp);
                publicCacheHttp.defaults.responseType = 'blob';
                return new Promise((resolve) => {
                    publicCacheHttp.post(url, data, { headers: { isJson } }).then(response => {
                        this.downFile(response, data['fileName'], data['enumFileType']);
                        resolve(response);
                    }).catch(err => {
                        console.log('err:===', err);
                    });
                });
                break;
        }
    }

    public downFile(response, fileName: string | undefined = undefined, fileType: string | undefined = undefined): void {
        console.log(response, fileName, fileType);
        const data = response.data;
        const blob = new Blob([data], { type: fileType });
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('style', 'display:none');
        a.setAttribute('href', objectUrl);
        a.setAttribute('download', fileName);
        a.click();
        URL.revokeObjectURL(objectUrl);
    }

    private async handleError(error) {
        const response = error.toJSON();
        const { status } = response;
        switch (status) {
            case StatusCode.InternalServerError: {
                store.dispatch(updateWarningMessage(error && error.response.data.message));
                break;
            }

            case StatusCode.AddAnalysisError: {
                store.dispatch(updateWarningMessage(error && error.response.data.message));
                break;
            }

            case StatusCode.Forbidden: {
                break;
            }

            case StatusCode.Unauthorized: {
                // if no token exist local storage
                try {
                    localStorage.removeItem('accessToken');
                    const signUrlRes = await soilGetSignUrl();

                    const redirectUrl = signUrlRes.url;
                    const codeVerifier = signUrlRes.codeVerifier;
                    localStorage.setItem('codeVerifier', codeVerifier);

                    store.dispatch(updateOauthUrl(redirectUrl));
                    store.dispatch(updateAuthenticateState(false));
                } catch (err) {
                    console.log('error');
                    // if the getsignUrl cannot request success it should be remind customer refresh current page
                    store.dispatch(updateRefreshModalShow(true));
                }
                break;
            }

            case StatusCode.TooManyRequests: {
                break;
            }
        }
        return response;
    }

    private configRequestPayloadFormat = (config: AxiosRequestConfig): AxiosRequestConfig => {
        try {
            if (config.method === 'post' && !config.headers.isJson && !config.headers.upload
                && (config.url === '/api/SoilProfile/GetSearchSoil' ||
                     config.url === '/api/SoilProfile/AddEditSoil' ||
                     config.url === '/api/SoilProfile/DeleteSoil')
            ) {
                config.headers['Content-Type'] = 'application/json; charset=utf-8';
            }
            else if (config.method === 'post' && !config.headers.isJson && !config.headers.upload) {
                config.headers['Content-Type'] = 'application/x-www-form-urlencoded';
                config.data = qs.stringify(config.data); // convert to formdata
                if (config.headers.upload) {
                    config.headers['Content-Type'] = 'multipart/form-data';
                }
            }
            if (config.method === 'post' && config.headers.upload &&
                (config.url === 'api/AgriAlertTemplate/UploadReportBanner')
            ){
                const fom = new FormData();
                for (const element of config.data) {
                    fom.append('files', element.fileContent[0]);
                }
                config.data = fom;
                config.headers['Content-Type'] = 'multipart/form-data; boundary=----WebKitFormBoundaryEqqBlXRVHXJDs7lt';
                config.headers['Accept'] = 'text/plain';
                return config;
            }

            if (config.method === 'post' && config.headers.upload &&
                (config.url === 'api/Feedback/SendFeedback')
            )
            {
                const fom = new FormData();
                //console.log('config.data.filesconfig.data.files', config.data.files);
                for (const element of config.data.files) {
                    //console.log('element', element.fileContent[0]);
                    fom.append('files', element.fileContent[0]);
                }
                fom.append('feedbackType', config.data.feedbackType);
                fom.append('feedbackSubject', config.data.feedbackSubject);
                fom.append('content', config.data.content);
                config.data = fom;
                config.headers['Content-Type'] = 'multipart/form-data';
                config.headers['Accept'] = 'Application/json';
            } else if (config.method === 'post' && config.headers.upload &&
                (config.url !== 'api/AgriAlertTemplate/UploadReportBanner')
            ) {
                console.log(config.data);
                const fom = new FormData();
                fom.append('file', config.data.file);
                config.data = fom;
                config.headers['Content-Type'] = 'multipart/form-data';
                config.headers['Accept'] = 'Application/json';
            }
            else if(config.url === '/api/AgriAlertTemplate/GetSprayWindowRecommendation'){
                config.headers['Accept'] = 'text/plain';
                config.headers['Content-Type'] = 'application/json; charset=utf-8';
            }

            return config;
        } catch (error) {
            return error;
        }
    };

    private configTokenBeforeAll = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig<unknown>> => {
        this.token = localStorage.getItem('accessToken');
        // this.token = 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6InMxMDc2MjgxIiwiZW1haWwiOiJrYWltaW4udGFuQHN5bmdlbnRhLmNvbSIsImdpdmVuX25hbWUiOiJ0YW4ga2FpbWluIChleHQpIENOQkoiLCJuYW1laWQiOiJrYWltaW4iLCJwcmVzZW50X2pzb24iOiJ7XCJVc2VySWRcIjpcInMxMDc2MjgxXCIsXCJVc2VyRW1haWxcIjpcImthaW1pbi50YW5Ac3luZ2VudGEuY29tXCIsXCJEaXNwbGF5TmFtZVwiOlwidGFuIGthaW1pbiAoZXh0KSBDTkJKXCIsXCJVc2VyU2ltcGxpZnlOYW1lXCI6XCJLVFwiLFwiVXNlckZpcnN0TmFtZVwiOlwia2FpbWluXCJ9IiwiaWF0IjoiMTczNzM0MDY5MSIsIm5iZiI6IjE3MzczNDA2OTEiLCJleHAiOiIxNzM3NDI3MDkxIiwiaXNzIjoiUG9ydGFsQ2VodWJJc3N1ZXIiLCJhdWQiOiJDRUh1YlBvcnRhbEF1ZGllbmNlIn0.o2peurmCYx3QKwfC4K56Z1Wyjlk8ccj8Z_kRbpBnYPs';
        if (config.url === '/api/Home/GetSignInUrl') {
            return config;
        }

        if (this.getTokenStatus === GetTokenStatus.TokenRequested || config.url.includes('/api/Home/GetToken') || this.token !== null) {
            this.token && (config.headers.Authorization = this.token);
            return config;
        } else {
            if (this.getTokenStatus === GetTokenStatus.TokenUnRequest && this.token === null) {
                try {
                    this.getTokenStatus = GetTokenStatus.TokenRequesting;
                    const { hash } = location;
                    if (hash.length > 0) {
                        const result = hash.replace('#code=', '');
                        const codeVerifier = await localStorage.getItem('codeVerifier');
                        const authorizedCode = result.split('&')[0];
                        const tokenRes = await soilGetUserToken({ authorizedCode, codeVerifier });
                        this.token = tokenRes.data.token;
                        const user = tokenRes.data.user;
                        store.dispatch(updateAuthenticateState(true));
                        store.dispatch(updateFirstName(user.userFirstName));
                        await localStorage.setItem('accessToken', this.token);
                        await localStorage.setItem('userFirstName', user.userFirstName);
                        await localStorage.removeItem('codeVerifier');

                        this.token && (config.headers.Authorization = this.token);
                        this.getTokenStatus = GetTokenStatus.TokenRequested;
                        this.requests.forEach((cb) => cb(this.token));
                        this.requests = [];
                        return config;
                    }
                    const signUrlRes = await soilGetSignUrl();

                    const redirectUrl = signUrlRes.url;
                    const codeVerifier = signUrlRes.codeVerifier;
                    localStorage.setItem('codeVerifier', codeVerifier);

                    store.dispatch(updateOauthUrl(redirectUrl));
                    return config;
                } catch (err) {
                    console.log('request for token error in https');
                }
            } else {

                return new Promise((resolve => {
                    this.requests.push((token) => {
                        token && (config.headers.Authorization = token);
                        resolve(config);
                    });
                }));
            }
        }

    };
}

const http = new Http();

export {
    http,
    ServerType
};
